import dynamic from "next/dynamic";
import { NextRouter, useRouter } from "next/router";
import cx from "classnames";
import { gql } from "utils/urql";
import { useState, useEffect, useRef, forwardRef } from "react";
import {
  ArticleBodyContentFragment,
  ArticleQuery,
  ArticleBentoRecircSwanFragment,
  ArticleRelatedContentType,
  ArticleJsonLdReviewFragment,
} from "types/api";
import { InjectedTypes } from "types/injector";
import styles from "./ArticleBody.module.scss";
import ArticleParagraph from "components/article/ArticleParagraph";
import ArticleRelatedContentLink from "components/article/ArticleRelatedContentLink";
import ArticlePullquote from "components/article/ArticlePullquote";
import ArticleInlineImage from "components/article/ArticleInlineImage";
import ArticleOverflowInlineImage from "components/article/ArticleOverflowInlineImage";
import ArticleWellInlineImage from "components/article/ArticleWellInlineImage";
import ArticleRelatedContentModule from "components/article/ArticleRelatedContentModule";
import ArticleLegacyHtml from "components/article/ArticleLegacyHtml";
import ArticleHeading from "components/article/ArticleHeading";
import ArticleInjectorAd from "components/ads/ArticleInjector";
import { injectItems } from "utils/csi/injector";
import useMedia from "utils/useMedia";
import { useActiveTestGroup } from "utils/abTesting";
import { useArticleContext } from "utils/articleContext";
import { getDataLayer } from "utils/analytics";

// The breakpoints at which the injected recirc and books module elements float.
const ArticleBooksFooter = dynamic(
  () => import("components/article/ArticleBooksFooter")
);

// The breakpoint at which the injected recirc element floats.
const RECIRC_BREAKPOINT = 576;
const BOOKS_MODULE_BREAKPOINT = 576;

/**
 * Loop through the article content and check if different types of elements already exist within it
 */
const csiContentTypenameCheck = (
  els: { __typename: string; contentType?: ArticleRelatedContentType }[]
): {
  hasRecirc: boolean;
  hasMagPromo: boolean;
  hasAdPlaceholder: boolean;
} => {
  let hasRecirc = false;
  let hasMagPromo = false;
  let hasAdPlaceholder = false;

  for (const el of els) {
    switch (el.__typename) {
      case "ArticleRelatedContentModule":
        if (el.contentType == ArticleRelatedContentType.Magazine) {
          hasMagPromo = true;
        } else {
          hasRecirc = true;
        }
        break;
      case "ArticleRelatedContentMagazine":
        hasMagPromo = true;
        break;
      case "ArticleAdPlaceholder":
        hasAdPlaceholder = true;
        break;
    }
  }

  return {
    hasRecirc,
    hasMagPromo,
    hasAdPlaceholder,
  };
};

/**
 * Check if the article is a part of a larger editorial project, uses Bento
 */
const hasBento = (
  editorialProject: ArticleBentoRecircSwanFragment | undefined | null
) => {
  if (editorialProject) {
    return editorialProject?.river.edges?.length > 3;
  }
  return false;
};

/**
 * Check if the article has a book review
 */
const hasBookReviews = (reviews: ArticleJsonLdReviewFragment[]) => {
  return reviews.some((review) => review.__typename === "BookReview");
};

/**
 * Check if an article was categorized to exclude the recirc module
 */
const excludeRecirc = (articleBody: ArticleQuery["article"]) => {
  return articleBody.categories.some(
    (cat) => cat.slug === "exclude-from-recirc"
  );
};

/**
 * Determine if we are currently serving a page in the mobile app
 */
const isAppWebview = (router: NextRouter) => {
  return router && router.pathname === "/app/webview/[id]";
};

/**
 * A default empty div component when we find an unrecognized component type in the content.
 * This ensures we're returning a clean array of JSX.Elements from buildComponentsArr
 */
const ArticleEmptyElement = forwardRef(function ArticleEmptyElement(
  props,
  ref: <T extends unknown>(el: T) => T
) {
  return <div ref={ref} {...props}></div>;
});

const ArticleBody = (articleBody: ArticleQuery["article"]) => {
  const activeTestGroup = useActiveTestGroup("Text-Only-Recommended-Reading");

  const refs = useRef<Array<HTMLElement>>([]);
  const [isMobile, isRecircFullwidth, booksModuleFullwidth] = useMedia(
    "(max-width: 975px)",
    `(max-width: ${RECIRC_BREAKPOINT}px)`,
    `(max-width: ${BOOKS_MODULE_BREAKPOINT}px)`
  );
  const { articleId } = useArticleContext();
  const router = useRouter();

  // isBooksFullwidth = true when books length is greater than 2 or viewport
  //is less than 576 (when inset books module goes to full width)
  const numBookReviews = articleBody.reviews.filter(
    (review) => review.__typename === "BookReview"
  ).length;
  const isBooksFullwidth = numBookReviews > 2 || booksModuleFullwidth;

  // make an array of JSX.Elements for each contentFragment.content
  const buildComponentsArr = ({
    content,
  }: ArticleBodyContentFragment): JSX.Element[] => {
    let recircModuleCount = 0;
    let recircLinkCount = 0;
    return content.map((element, i) => {
      const key = `position-${i}`;
      // make a function store the generated ref in the refs array so we can pass it to the injector
      const refFunc = (el: HTMLElement | null) => {
        if (el) {
          refs.current[i] = el;
        }
      };
      // All article element components must have a correctly forwarded ref or the injector
      // will not run
      switch (element?.__typename) {
        case "ArticleParagraphContent":
          return <ArticleParagraph key={key} ref={refFunc} {...element} />;
        case "ArticleHeading":
          return <ArticleHeading key={key} ref={refFunc} {...element} />;
        case "ArticleRelatedContentLink":
          recircLinkCount++;
          return (
            <ArticleRelatedContentLink
              count={recircLinkCount}
              key={key}
              ref={refFunc}
              {...element}
            />
          );
        case "ArticlePullquote":
          return <ArticlePullquote key={key} ref={refFunc} {...element} />;
        case "ArticleInlineImage":
          return <ArticleInlineImage key={key} ref={refFunc} {...element} />;
        case "ArticleWellInlineImage":
          return (
            <ArticleWellInlineImage key={key} ref={refFunc} {...element} />
          );
        case "ArticleOverflowInlineImage":
          return (
            <ArticleOverflowInlineImage key={key} ref={refFunc} {...element} />
          );
        case "ArticleRelatedContentModule":
          if (
            element.contentType === ArticleRelatedContentType.Curated ||
            element.contentType === ArticleRelatedContentType.Channel
          )
            recircModuleCount++;
          return (
            <ArticleRelatedContentModule
              count={recircModuleCount}
              key={key}
              ref={refFunc}
              {...element}
            />
          );
        case "ArticleAdPlaceholder":
          return (
            <ArticleInjectorAd
              key={key}
              ref={refFunc}
              targetingPos="manual-injector"
              {...element}
            />
          );
        case "ArticleLegacyHtml":
          return <ArticleLegacyHtml key={key} {...element} ref={refFunc} />;
        default:
          return <ArticleEmptyElement key={key} ref={refFunc} />;
      }
    });
  };

  // store the components we'll render in state so we can push injected elements into the array
  const [components, setComponents] = useState(buildComponentsArr(articleBody));
  const endRef = useRef<HTMLDivElement>(null);
  // after we've rendered once, run the client-side injector. Don't re-inject unless
  // the content changes or we switch from moble to desktop or vice versa
  useEffect(() => {
    const newComponents = buildComponentsArr(articleBody);
    const {
      hasRecirc,
      hasAdPlaceholder,
      hasMagPromo,
    } = csiContentTypenameCheck(articleBody.content);

    if (hasAdPlaceholder) {
      // don't inject anything new if the article already has ads
      setComponents(newComponents);
    } else {
      const { newComponentsArr, alreadyInjected } = injectItems(
        newComponents,
        refs,
        {
          activeTestGroup,
          isMobile,
          isRecircFullwidth,
          isBooksFullwidth,
          injectOrderOptions: {
            // only inject a recirc module if it doesn't already have a recirc module
            // and if it doesn't have a bento recirc module
            injectRecirc:
              !excludeRecirc(articleBody) &&
              !hasRecirc &&
              !hasBento(articleBody.editorialProject) &&
              !(
                activeTestGroup === "Text-Only-Recommended-Reading.hidden" &&
                isRecircFullwidth
              ),
            injectBooksModule: hasBookReviews(articleBody.reviews),
            injectMagPromo:
              articleBody.__typename === "MagazineArticle" && !hasMagPromo,
            isWebAppView: isAppWebview(router),
          },
          articleId,
        }
      );

      // Check if a books review module was injected
      const booksModule = alreadyInjected.find(
        (injected) => injected.type === InjectedTypes.Books
      );

      /*
       ** If there are book reviews, but no books module injected, insert a books footer with disclosure
       ** OR if the books review module was injected far enough away from the end of the article,
       ** insert a books footer with a disclosure.
       */
      const minComponentsTillEnd = 10;
      const componentsTillEnd =
        booksModule && articleBody.content.length - booksModule.afterIndex;
      if (
        (numBookReviews > 0 && !booksModule) ||
        (componentsTillEnd && componentsTillEnd >= minComponentsTillEnd)
      ) {
        // add a books footer to the end of the article
        newComponentsArr.push(
          <ArticleBooksFooter
            key={`position-${articleBody.content.length + 1}`}
            disclosure={numBookReviews > 0 && !booksModule}
          />
        );
        endRef.current?.classList.remove(styles.partialDivider);
        endRef.current?.classList.add(styles.fullDivider);
      }
      setComponents(newComponentsArr);
      /*
       * Analytics
       * Related Content module's 1-based index relative to component length
       */
      const relatedContentIndex = newComponentsArr.findIndex(
        (component) =>
          component.props.__typename === "ArticleRelatedContentModule"
      );
      if (relatedContentIndex > -1) {
        const totalComponents = newComponentsArr.length;
        getDataLayer().push({
          event: "Related Content Location",
          relatedContentLocation: `${
            relatedContentIndex + 1
          } - ${totalComponents}`,
        });
      }
    }
    // only compare the properties that would cause the injector rules to change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleBody.content, isMobile, activeTestGroup]);

  return (
    <section className={cx(styles.root)}>
      {components}
      <div
        ref={endRef}
        className={styles.partialDivider}
        id="article-end"
        style={{ height: `1px` }}
      />
    </section>
  );
};

ArticleBody.fragment = gql`
  ${ArticleInlineImage.fragment}
  ${ArticleOverflowInlineImage.fragment}
  ${ArticleWellInlineImage.fragment}
  ${ArticleLegacyHtml.fragment}
  ${ArticlePullquote.fragment}
  ${ArticleRelatedContentModule.fragment}
  ${ArticleParagraph.fragment}
  ${ArticleRelatedContentLink.fragment}
  ${ArticleHeading.fragment}

  fragment articleBodyContent on Article {
    content {
      __typename
      ...articleInlineImage
      ...articleOverflowInlineImage
      ...articleWellInlineImage
      ...articleLegacyHtml
      ...articlePullquote
      ...articleRelatedContentModule
      ...articleParagraph
      ...articleRelatedContentLink
      ...articleHeading
    }
    categories {
      slug
    }
  }
`;

export default ArticleBody;
